import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Plus, ArrowUpRight, Users } from 'lucide-react';
import { api } from '@/lib/api';

export default function Overview({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => { api('/dashboard').then(setData); }, []);

  const firstName = user.name.split(' ')[0];
  const scopeCaption = data?.scope === 'team'
    ? `Scoped to your team · ${data.team_vessels.length} vessel${data.team_vessels.length === 1 ? '' : 's'}`
    : (data?.scope === 'fleet' ? 'Across your fleet' : 'Across your vessel');
  const metrics = [
    ['Active crew', data?.active_crew ?? '—', scopeCaption],
    ['Open cases', data?.open_cases ?? '—', 'Need attention'],
    ['Pending grievances', data?.pending_grievances ?? '—', 'Awaiting resolution'],
    ['Follow-ups due', data?.follow_ups ?? '—', 'Open reminders'],
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">EMPLOYEE RELATIONS WORKSPACE</span>
          <h1>Good day, {firstName}.</h1>
          <p className="muted">Here's the current pulse of your employee relations workspace.</p>
        </div>
        <NavLink data-testid="overview-new-case-button" className="primary-btn" to="/cases?new=1"><Plus size={17} /> New case</NavLink>
      </div>
      <div className="metric-grid">
        {metrics.map((m, i) => (
          <div data-testid={`metric-${i}`} className="metric" key={m[0]}>
            <span>{m[0]}</span><strong>{m[1]}</strong><small>{m[2]}</small>
          </div>
        ))}
      </div>
      <div className="overview-grid">
        <section className="panel chart-panel">
          <div className="panel-title">
            <div><span className="eyebrow">CASE ACTIVITY</span><h2>Cases opened over time</h2></div>
            <span className="live-dot">● Live view</span>
          </div>
          <div className="chart">
            <div className="chart-bars">
              {(data?.monthly || []).map((m, i) => (
                <div className="bar-wrap" key={m.label}>
                  <div data-testid={`chart-bar-${i}`} className="bar" style={{ height: `${m.value * 7}px` }}>
                    <span>{m.value}</span>
                  </div>
                  <small>{m.label}</small>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section className="panel attention">
          <div className="panel-title">
            <div><span className="eyebrow">NEEDS ATTENTION</span><h2>Today's focus</h2></div>
            <NavLink data-testid="view-all-cases-link" to="/cases">View all <ArrowUpRight size={14} /></NavLink>
          </div>
          <div className="focus-item"><div className="focus-icon red">!</div><div><strong>Hearing scheduled</strong><p>AG-2026-0137 · Sofia Duarte</p></div><span>28 Jun</span></div>
          <div className="focus-item"><div className="focus-icon blue">↗</div><div><strong>Witness interview</strong><p>AG-2026-0142 · Amelia Tan</p></div><span>24 Jun</span></div>
          <div className="focus-item"><div className="focus-icon amber">◷</div><div><strong>Grievance review</strong><p>AG-2026-0139 · Marcus Webb</p></div><span>22 Jun</span></div>
        </section>
      </div>
      {user.role === 'corporate_hr' && data?.team?.length > 0 && (
        <section className="panel" style={{ marginTop: 14 }} data-testid="my-team-panel">
          <div className="panel-title">
            <div><span className="eyebrow">MY TEAM</span><h2><Users size={16} style={{ display: 'inline', marginRight: 6 }} /> Vessel HR reports</h2></div>
            <NavLink data-testid="team-manage-link" to="/admin">Manage <ArrowUpRight size={14} /></NavLink>
          </div>
          <div className="admin-list" style={{ marginTop: 12 }}>
            {data.team.map((t) => (
              <div className="admin-row" key={t.id} data-testid={`team-report-${t.id}`}>
                <div>
                  <span className="role-badge vessel_hr">Vessel</span>
                  <strong>{t.name}</strong>
                  <small className="muted"> · {t.vessel || 'Unassigned'} · {t.email}</small>
                </div>
                <div>
                  <span className="metric-inline" data-testid={`team-open-${t.id}`}>{t.open_cases}</span>
                  <span className="muted"> open case{t.open_cases === 1 ? '' : 's'}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
